import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { offerProcessingService } from './offer-processing.service';
import { OfferListItemDto, UploadOfferResultDto } from './offer-processing.dto';
import { isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { PaginatedResponseDto } from 'src/infrastructure/admi-client/response.dto';
export interface State {
    isLoading: boolean;
    uploadResult: UploadOfferResultDto | null;
    errorMessage: string | null;
    // list state
    isLoadingList: boolean;
    items: PaginatedResponseDto<OfferListItemDto>;

    // track which rows are busy (retry/delete/download)
    rowPending: Record<string, { retry?: boolean; delete?: boolean; details?: boolean }>;
}

const initialState: State = {
    isLoading: false,
    uploadResult: null,
    errorMessage: null,
    isLoadingList: false,
    items: {
        content: [],
        page: { size: 0, totalElements: 0, totalPages: 0, number: 0 },
    },
    rowPending: {},
};

export const uploadOfferFile = createAsyncThunk('offerProcessing/uploadFile', async (file: File, _thunkApi) => {
    try {
        return await offerProcessingService.uploadOfferFile(file);
    } catch (err: unknown) {
        return _thunkApi.rejectWithValue(err);
    }
});

export const fetchOffers = createAsyncThunk(
    'offerProcessing/fetchOffers',
    async (params: { page: number; perPage: number; searchString: string }, _thunkApi) => {
        try {
            const { page, perPage, searchString } = params;
            const response = await offerProcessingService.listOffers(page, perPage, searchString);
            return response;
        } catch (err: unknown) {
            return _thunkApi.rejectWithValue(err);
        }
    },
);

export const fetchOfferDetails = createAsyncThunk(
    'offerProcessing/fetchOfferDetails',
    async (fileId: string, _thunkApi) => {
        try {
            const response = await offerProcessingService.getOffer(fileId);
            return response;
        } catch (err: unknown) {
            return _thunkApi.rejectWithValue(err);
        }
    },
);

export const retryOffer = createAsyncThunk('offerProcessing/retryOffer', async (id: string, _thunkApi) => {
    try {
        await offerProcessingService.retryOffer(id);
        return id;
    } catch (err: unknown) {
        return _thunkApi.rejectWithValue(err);
    }
});

export const deleteOffer = createAsyncThunk('offerProcessing/deleteOffer', async (id: string, _thunkApi) => {
    try {
        await offerProcessingService.deleteOffer(id);
        return id;
    } catch (err: unknown) {
        return _thunkApi.rejectWithValue(err);
    }
});

const offerProcessingSlice = createSlice({
    name: 'offerProcessing',
    initialState,
    reducers: {
        clearUploadResult(state) {
            state.uploadResult = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(uploadOfferFile.fulfilled, (state, action) => {
            state.uploadResult = action.payload;
            state.isLoading = false;
        });

        builder.addCase(uploadOfferFile.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(uploadOfferFile.rejected, (state, action) => {
            console.debug('uploadOfferFile/rejected', action.payload);
            state.isLoading = false;
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed uploading the file');
        });

        // List
        builder.addCase(fetchOffers.pending, (state) => {
            state.isLoadingList = true;
        });
        builder.addCase(fetchOffers.fulfilled, (state, action) => {
            state.items = action.payload;
            state.isLoadingList = false;
        });
        builder.addCase(fetchOffers.rejected, (state, action) => {
            state.isLoadingList = false;
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to load the list of offers');
        });

        // Retry
        builder.addCase(retryOffer.pending, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], retry: true };
        });
        builder.addCase(retryOffer.fulfilled, (state, action) => {
            const id = action.payload;
            state.rowPending[id] = { ...state.rowPending[id], retry: false };
            // optionally set status to PROCESSING optimistically
            const row = state.items.content.find((i) => i.fileId === id);
            if (row) row.status = 'PROCESSING' as any;
        });
        builder.addCase(retryOffer.rejected, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], retry: false };
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to invoke retry');
        });

        // Delete
        builder.addCase(deleteOffer.pending, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], delete: true };
        });
        builder.addCase(deleteOffer.fulfilled, (state, action) => {
            const id = action.payload;
            state.rowPending[id] = { ...state.rowPending[id], delete: false };
            state.items.content = state.items.content.filter((i) => i.fileId !== id);
        });
        builder.addCase(deleteOffer.rejected, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], delete: false };
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to delete the offer');
        });

        // Fetch details
        builder.addCase(fetchOfferDetails.fulfilled, (state, action) => {
            const details = action.payload;
            const id = details.fileId;
            state.rowPending[id] = { ...state.rowPending[id], details: false };
            const row = state.items.content.find((i) => i.fileId === details.fileId);
            if (row) {
                Object.assign(row, details);
            }
        });
        builder.addCase(fetchOfferDetails.rejected, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], details: false };
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to load the offer details');
        });
        builder.addCase(fetchOfferDetails.pending, (state, action) => {
            const id = action.meta.arg;
            state.rowPending[id] = { ...state.rowPending[id], details: true };
        });
    },
});

export const { clearUploadResult } = offerProcessingSlice.actions;
export default offerProcessingSlice.reducer;
